import React, { useState, useEffect, useCallback } from 'react';
import { Package } from '../../types/Package';
import { getOptimizedImageUrl } from '../../utils/CloudinaryUtils';
import { formatCurrency, convertCurrencyWithMarkup, loadCurrencySettings } from '../../utils/CurrencyUtils';
import { useCurrency } from '../../contexts/CurrencyContext';
import { useDisplaySettings } from '../../hooks/useDisplaySettings';
import ShareButton from '../Sharing/ShareButton';
import { generateShareContent } from '../../utils/sharingUrls';
import { handleWhatsAppContact } from '../../utils/whatsappContact';
import { loadCategoriesFromServer, Category } from '../../utils/categories';
import SharePackagesModal from '../Packages/SharePackagesModal';
import './PackagesGrid.css';

interface PackagesGridProps {
  packages: Package[];
  onPackageClick?: (packageItem: Package) => void;
  showAll?: boolean;
  maxItems?: number;
  categoryFilter?: string | null;
  onPackageFinderClick?: () => void;
  showSharePackages?: boolean; // Show "Share Packages" instead of "Package Finder"
}

const PackagesGrid: React.FC<PackagesGridProps> = ({
  packages,
  onPackageClick,
  showAll = false,
  maxItems = 3,
  categoryFilter = null,
  onPackageFinderClick,
  showSharePackages = false
}) => {
  const displaySettings = useDisplaySettings();
  const [categories, setCategories] = useState<Category[]>([]);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);

  // Filter packages by category if categoryFilter is set
  const filteredPackages = categoryFilter 
    ? packages.filter(p => p.available && p.category?.toLowerCase() === categoryFilter.toLowerCase())
    : packages.filter(p => p.available);

  // Filter featured packages or take first few
  // Note: If packages prop already contains only featured packages (from HomePage),
  // we still filter to ensure consistency
  const featuredPackages = filteredPackages.filter(p => p.featured && p.available);
  const effectiveMaxItems = maxItems || 3;
  
  const displayPackages = showAll 
    ? filteredPackages
    : (featuredPackages.length > 0 ? featuredPackages : filteredPackages).slice(0, effectiveMaxItems);

  // Load categories
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const loadedCategories = await loadCategoriesFromServer();
        setCategories(loadedCategories);
      } catch (error) {
        console.error('Error loading categories:', error);
      }
    };
    loadCategories();
  }, []);

  if (displayPackages.length === 0) {
    return (
      <div className="packages-grid-empty">
        <div className="empty-state">
          <div className="empty-icon">
            <i className="fas fa-gift"></i>
          </div>
          <h3>No Packages Available</h3>
          <p>Check back soon for my photography packages</p>
        </div>
      </div>
    );
  }

  const cardStyle = displaySettings?.packageCardStyle || 'premium';
  const cardSpacingRem = displaySettings?.packageCardSpacing ?? 4;

  return (
    <section className="packages-grid-section">
      <div className="packages-container">
        {!showAll && (
          <div className="packages-header">
            <div className="header-content">
              <h2 className="packages-title">
                Featured Packages
              </h2>
              <p className="packages-subtitle">Professional packages tailored to capture your story</p>
            </div>
            
            <div className="header-action">
              {onPackageFinderClick ? (
                <button 
                  className="view-all-btn find-package-btn"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onPackageFinderClick();
                  }}
                  title="Find your perfect package"
                  type="button"
                >
                  <i className="fas fa-search"></i>
                  <span>Package Finder</span>
                </button>
              ) : (
                <a href="/packages" className="view-all-btn">
                  <span>View All Packages</span>
                  <i className="fas fa-arrow-right"></i>
                </a>
              )}
            </div>
          </div>
        )}

        <div 
          className={`packages-grid grid-${displaySettings?.packageCardColumns || (displayPackages.length === 2 ? 2 : displayPackages.length >= 3 ? 3 : 1)}`}
          style={{
            '--package-image-height': `${displaySettings?.packageCardImageHeight || 240}px`,
            '--package-card-spacing': `${cardSpacingRem}rem`,
            '--package-card-radius': `${displaySettings?.packageCardBorderRadius || 13}px`,
            '--package-card-shadow': displaySettings?.packageCardShadow || 'medium',
            gap: `${cardSpacingRem}rem`
          } as React.CSSProperties}
        >
          {displayPackages.map((packageItem, index) => (
            <PackageCard
              key={packageItem.id}
              package={packageItem}
              onClick={onPackageClick}
              delay={index * 0.1}
              categories={categories}
              cardStyle={cardStyle}
            />
          ))}
        </div>

        {!showAll && (
          <div className="packages-footer">
            {showSharePackages ? (
              <button 
                className="view-all-btn view-all-btn-center find-package-btn"
                onClick={() => setIsShareModalOpen(true)}
                title="Share packages with others"
              >
                <i className="fas fa-share-alt"></i>
                <span>Share Packages</span>
              </button>
            ) : onPackageFinderClick ? (
              <button 
                className="view-all-btn view-all-btn-center find-package-btn"
                onClick={onPackageFinderClick}
                title="Find your perfect package"
              >
                <i className="fas fa-search"></i>
                <span>Package Finder</span>
              </button>
            ) : (
              <a href="/packages" className="view-all-btn view-all-btn-center">
                <span>View All Packages</span>
                <i className="fas fa-arrow-right"></i>
              </a>
            )}
          </div>
        )}
      </div>
      
      {/* Share Packages Modal */}
      <SharePackagesModal
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
      />
    </section>
  );
};

// Package Card Component with currency conversion
const PackageCard: React.FC<{
  package: Package;
  onClick?: (packageItem: Package) => void;
  delay?: number;
  categories?: Category[];
  cardStyle?: string;
}> = ({ package: packageItem, onClick, delay = 0, categories = [], cardStyle = 'premium' }) => {
  const { detectedCurrency, isDetecting } = useCurrency();
  const [convertedPrices, setConvertedPrices] = useState<{
    displayPrice: number | null;
    originalPrice: number | null;
    discountAmount: number | null;
    currency: string;
  }>({
    displayPrice: null,
    originalPrice: null,
    discountAmount: null,
    currency: packageItem.currency || 'R'
  });

  // Determine the base prices
  const baseDisplayPrice = packageItem.isSpecial && packageItem.specialPrice ? packageItem.specialPrice : packageItem.price;
  const baseOriginalPrice = packageItem.price;
  const baseDiscountAmount = Boolean(
    packageItem.isSpecial && 
    packageItem.specialPrice && 
    packageItem.price && 
    packageItem.specialPrice < packageItem.price
  ) ? (packageItem.price! - packageItem.specialPrice!) : 0;

  const hasDiscount = Boolean(
    packageItem.isSpecial && 
    packageItem.specialPrice && 
    packageItem.price && 
    packageItem.specialPrice < packageItem.price
  );

  // Convert prices when currency or package changes
  useEffect(() => {
    const convertPrices = async () => {
      if (!baseDisplayPrice || isDetecting) {
        // Use original prices while detecting or if no price
        setConvertedPrices({
          displayPrice: baseDisplayPrice || null,
          originalPrice: baseOriginalPrice || null,
          discountAmount: baseDiscountAmount || null,
          currency: packageItem.currency || 'R'
        });
        return;
      }

      const packageCurrency = packageItem.currency || 'R';
      
      // If currencies match, no conversion needed
      if (detectedCurrency === packageCurrency) {
        setConvertedPrices({
          displayPrice: baseDisplayPrice,
          originalPrice: baseOriginalPrice || null,
          discountAmount: baseDiscountAmount || null,
          currency: packageCurrency
        });
        return;
      }

      // Convert prices
      try {
        const currencySettings = loadCurrencySettings();
        
        // Convert display price
        let convertedDisplayPrice = baseDisplayPrice;
        if (baseDisplayPrice) {
          const displayConversion = await convertCurrencyWithMarkup(
            baseDisplayPrice,
            packageCurrency,
            detectedCurrency,
            currencySettings
          );
          convertedDisplayPrice = displayConversion.markupAmount;
        }

        // Convert original price
        let convertedOriginalPrice = baseOriginalPrice;
        if (baseOriginalPrice) {
          const originalConversion = await convertCurrencyWithMarkup(
            baseOriginalPrice,
            packageCurrency,
            detectedCurrency,
            currencySettings
          );
          convertedOriginalPrice = originalConversion.markupAmount;
        }

        // Convert discount amount
        let convertedDiscountAmount = baseDiscountAmount;
        if (baseDiscountAmount > 0) {
          const discountConversion = await convertCurrencyWithMarkup(
            baseDiscountAmount,
            packageCurrency,
            detectedCurrency,
            currencySettings
          );
          convertedDiscountAmount = discountConversion.markupAmount;
        }

        setConvertedPrices({
          displayPrice: convertedDisplayPrice,
          originalPrice: convertedOriginalPrice || null,
          discountAmount: convertedDiscountAmount || null,
          currency: detectedCurrency
        });
      } catch (error) {
        console.error('Error converting currency:', error);
        // Fallback to original prices on error
        setConvertedPrices({
          displayPrice: baseDisplayPrice,
          originalPrice: baseOriginalPrice || null,
          discountAmount: baseDiscountAmount || null,
          currency: packageCurrency
        });
      }
    };

    convertPrices();
  }, [baseDisplayPrice, baseOriginalPrice, baseDiscountAmount, packageItem.currency, detectedCurrency, isDetecting]);

  const displayPrice = convertedPrices.displayPrice ?? baseDisplayPrice;
  const originalPrice = convertedPrices.originalPrice ?? baseOriginalPrice;
  const discountAmount = convertedPrices.discountAmount ?? baseDiscountAmount;
  const displayCurrency = convertedPrices.currency;

  // Generate share content with converted prices and currency
  const shareContent = generateShareContent('package', packageItem, {
    displayPrice: displayPrice,
    displayCurrency: displayCurrency,
    originalPrice: originalPrice
  });

  // Handle WhatsApp contact
  const handlePackageWhatsAppContact = (e: React.MouseEvent) => {
    e.stopPropagation();
    handleWhatsAppContact(packageItem.name, displayPrice ?? null, displayCurrency, originalPrice ?? null, packageItem.durationMinutes);
  };

  const formatDuration = (minutes: number): string => {
    const hours = minutes / 60;
    
    if (hours === 0.5) return '½ hour';
    if (hours === 0.25) return '¼ hour';
    if (hours === 0.75) return '¾ hour';
    if (hours === 1.5) return '1½ hours';
    if (hours === 2.5) return '2½ hours';
    if (hours === 3.5) return '3½ hours';
    if (hours === 4.5) return '4½ hours';
    if (hours === 5.5) return '5½ hours';
    if (hours === 6.5) return '6½ hours';
    if (hours === 7.5) return '7½ hours';
    if (hours === 8.5) return '8½ hours';
    
    if (hours % 1 === 0) {
      return `${hours} hour${hours > 1 ? 's' : ''}`;
    }
    
    return `${hours} hours`;
  };

  // Simple click handler - no delays, no async operations
  const handleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (onClick) {
      onClick(packageItem);
    }
  }, [onClick, packageItem]);

  const handleCardClick = useCallback((e: React.MouseEvent) => {
    // Whole card is clickable - only prevent if clicking on share button or other interactive elements
    const target = e.target as HTMLElement;
    if (target.closest('.package-share-button') || target.closest('.package-whatsapp-contact') || target.closest('a')) {
      return;
    }
    // Removed button check - whole card is now clickable
    if (onClick) {
      onClick(packageItem);
    }
  }, [onClick, packageItem]);

  return (
    <div 
      className={`package-card style-${cardStyle} ${packageItem.isSpecial ? 'special-branding' : ''} ${packageItem.badge ? `badge-${packageItem.badge.toLowerCase()}` : ''}`}
      style={{ animationDelay: `${delay}s` }}
      onClick={handleCardClick}
    >
      {/* Card Background Effects */}
      <div className="card-background">
        <div className="card-gradient"></div>
      </div>

      {/* Card Content - New Layout: Title, Description, Badges, Image, Price */}
      <div className="card-content">
        {/* 1. Title at Top */}
        <div className="card-header">
          <h3 className="package-title">{packageItem.name}</h3>
        </div>

        {/* 2. Short Description */}
        {packageItem.short && (
          <p className="package-short">{packageItem.short}</p>
        )}

        {/* 3. Duration Badge (and Category) */}
        <div className="package-meta-badges">
          {packageItem.durationMinutes && (
            <div className="meta-item meta-item-duration">
              <i className="fas fa-clock"></i>
              <span>{formatDuration(packageItem.durationMinutes)}</span>
            </div>
          )}
          {packageItem.categories && packageItem.categories.length > 0 ? (
            packageItem.categories.slice(0, 1).map((categorySlug: string, idx: number) => {
              const category = categories.find((c: Category) => c.slug === categorySlug);
              return (
                <div key={idx} className="meta-item meta-item-category">
                  <i className={getPackageIcon(category?.slug || categorySlug)}></i>
                  <span>{category?.name || categorySlug}</span>
                </div>
              );
            })
          ) : packageItem.category ? (
            <div className="meta-item meta-item-category">
              <i className={getPackageIcon(packageItem.category)}></i>
              <span>{packageItem.category}</span>
            </div>
          ) : null}
        </div>

        {/* 4. Package Image */}
        {packageItem.image ? (
          <div className="package-image">
            <img 
              src={getOptimizedImageUrl(packageItem.image, 'card')}
              alt={`${packageItem.name} - Photography Package`}
              loading="lazy"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
                const placeholder = e.currentTarget.parentElement?.querySelector('.package-image-placeholder') as HTMLElement;
                if (placeholder) {
                  placeholder.style.display = 'flex';
                }
              }}
            />
            <div className="package-image-placeholder" style={{ display: 'none' }}>
              <div className="placeholder-content">
                <i className="fas fa-camera"></i>
                <span>Package Image</span>
              </div>
            </div>
            
            {/* Share Button */}
            <div className="package-share-button" onClick={(e) => e.stopPropagation()}>
              <ShareButton
                content={shareContent}
                variant="icon"
                size="small"
                platforms={['whatsapp', 'twitter', 'facebook', 'pinterest', 'linkedin']}
                onShare={() => {}}
              />
            </div>
          </div>
        ) : (
          <div className="package-image package-image-placeholder">
            <div className="placeholder-content">
              <i className="fas fa-camera"></i>
              <span>Package Image</span>
            </div>
          </div>
        )}

        {/* 5. Price and Other Info */}
        <div className="pricing-section">
          <div className="price-display">
            {/* Base Price Label */}
            <div className="base-price-label-card">Base Price</div>
            {/* Current Price Row - On Top */}
            <div className={`current-price-row ${packageItem.isSpecial ? 'has-special' : 'no-special'}`}>
              <span className={`current-price ${!displayPrice ? 'price-request' : ''}`}>
                {displayPrice ? formatCurrency(displayPrice, displayCurrency) : 'Price on request'}
              </span>
              {packageItem.isSpecial && (
                <span className="special-star">
                  <i className="fas fa-star"></i>
                  <span className="special-text">Special</span>
                </span>
              )}
            </div>
            
            {/* Original Price Row - Always show, with placeholder if no discount */}
            <div className="price-row">
              {hasDiscount && originalPrice && displayPrice ? (
                <>
                  <span className="original-price">
                    {formatCurrency(originalPrice, displayCurrency)}
                  </span>
                  <span className="discount-badge">
                    Save {formatCurrency(discountAmount, displayCurrency)}
                  </span>
                </>
              ) : (
                <>
                  <span className="price-row-placeholder"></span>
                  <span className="price-row-placeholder"></span>
                </>
              )}
            </div>
            
            {/* Base Package Description - What's included */}
            {(() => {
              const baseIncluded = (packageItem as any).basePriceIncluded;
              const hasBaseIncluded = baseIncluded && typeof baseIncluded === 'string' && baseIncluded.trim().length > 0;
              const hasFeatures = packageItem.features && packageItem.features.length > 0;
              
              if (!hasBaseIncluded && !hasFeatures) return null;
              
              return (
                <div className="base-package-includes-card">
                  <div className="base-package-includes-label-card">
                    <i className="fas fa-check-circle"></i>
                    <span>Includes:</span>
                  </div>
                  {hasBaseIncluded ? (
                    <p className="base-package-description-card">{baseIncluded.trim()}</p>
                  ) : hasFeatures && packageItem.features ? (
                    <p className="base-package-description-card">
                      {packageItem.features.slice(0, 2).map(f => f.replace(/\*\*/g, '').replace(/\*/g, '')).join(' • ')}
                    </p>
                  ) : null}
                </div>
              );
            })()}
          </div>
        </div>
      </div>

      {/* Badge */}
      {packageItem.badge && (
        <div className={`package-badge ${packageItem.badge.toLowerCase()}`}>
          <span>{packageItem.badge}</span>
        </div>
      )}

      {/* Card Border Effect */}
      <div className="card-border"></div>
    </div>
  );
};

// Helper function to get appropriate icon based on package category
const getPackageIcon = (category?: string): string => {
  switch (category?.toLowerCase()) {
    case 'portraits':
      return 'fas fa-user-circle';
    case 'wedding':
      return 'fas fa-heart';
    case 'commercial':
      return 'fas fa-briefcase';
    case 'business':
      return 'fas fa-building';
    case 'event':
      return 'fas fa-calendar-star';
    case 'family':
      return 'fas fa-users';
    case 'newborn':
      return 'fas fa-baby';
    default:
      return 'fas fa-camera';
  }
};

export default PackagesGrid;
